import {Browser, Clipboard, Html, ModfierKeys} from "@opendaw/lib-dom"
import css from "./Markdown.sass?inline"
import {isDefined, Procedure} from "@opendaw/lib-std"
import {Promises} from "@opendaw/lib-runtime"
import {createElement, RouteLocation} from "@opendaw/lib-jsx"
import markdownit from "markdown-it"
import {markdownItTable} from "markdown-it-table"
import {IconSymbol} from "@opendaw/studio-enums"
import {Icon} from "@opendaw/studio-icons"
import {isManualsPath} from "./links"

const className = Html.adoptStyleSheet(css, "Markdown")

export type MarkdownOptions = {
    onCopied?: Procedure<HTMLElement>
}

type Construct = {
    text: string
} & MarkdownOptions

const replaceModifierKeys = (text: string): string => {
    if (!Browser.isWindows()) {return text}
    const keys = ["Cmd", "Opt", "Shift"] as const
    keys.forEach(key => {text = text.replaceAll(ModfierKeys.Mac[key], ModfierKeys.Win[key])})
    return text
}

export const renderMarkdown = (element: HTMLElement, text: string, options?: MarkdownOptions) => {
    const {onCopied} = options ?? {}
    text = replaceModifierKeys(text)
    const md = markdownit()
    md.use(markdownItTable)
    element.innerHTML = md.render(text)
    element.querySelectorAll("img").forEach(img => {
        img.crossOrigin = "anonymous"
        img.style.maxWidth = "100%"
    })
    element.querySelectorAll("a").forEach(anchor => {
        const url = new URL(anchor.href)
        if (url.origin !== location.origin || url.pathname.startsWith("/docs/")) {
            anchor.target = "_blank"
            return
        }
        if (isManualsPath(url.pathname)) {
            anchor.onclick = (event: Event) => {
                event.preventDefault()
                RouteLocation.get().navigateTo(url.pathname)
            }
        }
    })
    element.querySelectorAll("code").forEach(code => {
        code.title = "Click to copy to clipboard"
        code.onclick = async () => {
            if (isDefined(code.textContent)) {
                const {status} = await Promises.tryCatch(Clipboard.writeText(code.textContent))
                if (status === "resolved") {
                    onCopied?.(element)
                }
            }
        }
    })
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT)
    const iconPattern = /\{icon:(\w+)\}/g
    const nodesToReplace: Array<{ node: Text, matches: Array<{ match: string, name: string, index: number }> }> = []
    let node: Text | null
    while ((node = walker.nextNode() as Text | null)) {
        const matches: Array<{ match: string, name: string, index: number }> = []
        let match: RegExpExecArray | null
        while ((match = iconPattern.exec(node.textContent ?? "")) !== null) {
            matches.push({match: match[0], name: match[1], index: match.index})
        }
        if (matches.length > 0) {nodesToReplace.push({node, matches})}
    }
    for (const {node, matches} of nodesToReplace) {
        const parent = node.parentNode
        if (!isDefined(parent)) {continue}
        const content = node.textContent ?? ""
        let lastIndex = 0
        const fragment = document.createDocumentFragment()
        for (const {match, name, index} of matches) {
            if (index > lastIndex) {
                fragment.appendChild(document.createTextNode(content.slice(lastIndex, index)))
            }
            fragment.appendChild(<Icon symbol={IconSymbol.fromName(name)} className="icon"/>)
            lastIndex = index + match.length
        }
        if (lastIndex < content.length) {
            fragment.appendChild(document.createTextNode(content.slice(lastIndex)))
        }
        parent.replaceChild(fragment, node)
    }
}

export const Markdown = ({text, onCopied}: Construct) => {
    if (text.startsWith("<")) {return "Invalid Markdown"}
    const element: HTMLElement = <div className={Html.buildClassList(className, "markdown")}/>
    renderMarkdown(element, text, {onCopied})
    return element
}
